"use client";

import { c as _c } from "react/compiler-runtime";
import { useServices } from "../services-context";
import { useMemo } from "react";
import { useParams } from "next/navigation";
import { usesSelfScheduleCategoryHierarchy } from "@/lib/tenant-flags";
import { Category } from "@/types/category";
import { PublicLocationDetail } from "@/types/location";
import { PublicServiceDetail } from "@/types/service";
import { filterServices, getUnavailableServiceIds, sortServicesForSearch } from "./helpers";
import { createSelectionHandler } from "./selection";
import { useServiceFilterOptions } from "./use-service-filter-options";
import { FIRST_AVAILABLE_PROVIDER_ID } from "@/stores/self-schedule-store";
const TAGGED_SERVICES_THRESHOLD = 200;
export interface UseServicesViewProps {
  onOpenDetails?: (serviceId: string) => void;
  onRequireMfa?: (serviceId: string, onSelect?: (serviceId: string) => void) => void;
  onPhoneOnlyService?: (serviceId: string) => void;
  onSelected?: (serviceId: string) => void;
  onOpenSelectionsDrawer?: (serviceId: string) => void;
}
export interface FrontPageViewData {
  appointments: PublicServiceDetail[] | undefined;
  services: PublicServiceDetail[] | undefined;
  categories: Category[] | undefined;
  locations: PublicLocationDetail[] | undefined;
  shouldGroupServicesByCategory: boolean | undefined;
  shouldGroupServicesByLocation?: boolean | undefined;
  showServiceDuration: boolean | undefined;
}
export interface UseServicesViewResult {
  frontPageData: FrontPageViewData | undefined;
  categories: Category[];
  filteredServices: PublicServiceDetail[];
  isSearching: boolean;
  isLoading: boolean;
  selectedCategory: string | undefined;
  isServiceSelected: (id: string) => boolean;
  getServiceInstanceCount: (id: string) => number;
  isServiceUnavailable: (id: string) => boolean;
  selectedServiceIds: string[];
  canSelectMore: boolean;
  userLocation: {
    lat: number;
    lng: number;
    accuracy?: number;
  } | null;
  effectiveNumAllowedServices: number;
  shouldUseLargeTaggedLayout: boolean;
  useCategoryHierarchy: boolean;
  handleServiceClick: (serviceId: string) => void;
  handleQuantityChange: (serviceId: string, quantity: number) => void;
  handleInfoClick: (e: React.MouseEvent<HTMLElement | SVGElement>, serviceId: string) => void;
}
export function useServicesView(props) {
  const $ = _c(73);
  const {
    onOpenDetails,
    onRequireMfa,
    onPhoneOnlyService,
    onSelected,
    onOpenSelectionsDrawer
  } = props;
  const {
    state: t0,
    actions: t1
  } = useServices();
  const {
    data,
    isLoading,
    searchQuery,
    locationType,
    selectedLocationId,
    selectedLocation,
    patientType,
    userLocation,
    selectedServiceIds,
    selectedServices,
    selectedProviderId,
    effectiveNumAllowedServices
  } = t0;
  const {
    addService,
    removeService,
    removeAllServiceInstances,
    isServiceSelected,
    getServiceInstanceCount
  } = t1;
  const {
    categories,
    effectiveSelectedCategory
  } = useServiceFilterOptions();
  const providerFilterId = selectedProviderId === FIRST_AVAILABLE_PROVIDER_ID ? null : selectedProviderId;
  let t2;
  if ($[0] !== data) {
    t2 = data ? {
      appointments: data.services,
      services: data.services,
      categories: data.categories,
      locations: data.locations,
      shouldGroupServicesByCategory: data.shouldGroupServicesByCategory,
      shouldGroupServicesByLocation: data.shouldGroupServicesByLocation,
      showServiceDuration: data.showServiceDuration
    } : undefined;
    $[0] = data;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const frontPageData = t2;
  let t3;
  if ($[2] !== data?.categories || $[3] !== effectiveSelectedCategory || $[4] !== frontPageData?.appointments || $[5] !== frontPageData?.locations || $[6] !== locationType || $[7] !== patientType || $[8] !== providerFilterId || $[9] !== searchQuery || $[10] !== selectedLocation?.id || $[11] !== selectedLocationId) {
    t3 = () => {
      const services = filterServices(frontPageData?.appointments, {
        searchQuery,
        selectedCategory: effectiveSelectedCategory,
        locations: frontPageData?.locations,
        locationType,
        selectedLocationId: selectedLocation?.id ?? selectedLocationId,
        selectedProviderId: providerFilterId,
        patientType,
        categories: data?.categories
      });
      return sortServicesForSearch(services, searchQuery);
    };
    $[2] = data?.categories;
    $[3] = effectiveSelectedCategory;
    $[4] = frontPageData?.appointments;
    $[5] = frontPageData?.locations;
    $[6] = locationType;
    $[7] = patientType;
    $[8] = providerFilterId;
    $[9] = searchQuery;
    $[10] = selectedLocation?.id;
    $[11] = selectedLocationId;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  frontPageData?.appointments;
  selectedLocation?.id;
  frontPageData?.locations;
  data?.categories;
  let t4;
  if ($[13] !== t3) {
    t4 = t3();
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const filteredServices = t4;
  let t5;
  if ($[15] !== addService || $[16] !== effectiveNumAllowedServices || $[17] !== filteredServices || $[18] !== locationType || $[19] !== onOpenSelectionsDrawer || $[20] !== onPhoneOnlyService || $[21] !== onRequireMfa || $[22] !== onSelected || $[23] !== removeAllServiceInstances || $[24] !== selectedServiceIds) {
    t5 = createSelectionHandler(filteredServices, {
      addSelectedService: addService,
      removeAllSelectedServiceInstances: removeAllServiceInstances,
      selectedServiceIds,
      numAllowedServices: effectiveNumAllowedServices,
      onRequireMfa,
      onPhoneOnlyService,
      locationType,
      onSelected,
      onOpenSelectionsDrawer
    });
    $[15] = addService;
    $[16] = effectiveNumAllowedServices;
    $[17] = filteredServices;
    $[18] = locationType;
    $[19] = onOpenSelectionsDrawer;
    $[20] = onPhoneOnlyService;
    $[21] = onRequireMfa;
    $[22] = onSelected;
    $[23] = removeAllServiceInstances;
    $[24] = selectedServiceIds;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const handleServiceClick = t5;
  let t6;
  if ($[26] !== getServiceInstanceCount || $[27] !== handleServiceClick || $[28] !== removeService) {
    t6 = (serviceId, quantity) => {
      const current = getServiceInstanceCount(serviceId);
      if (quantity > current) {
        handleServiceClick(serviceId, "increment");
        return;
      }
      if (quantity < current) {
        removeService(serviceId);
      }
    };
    $[26] = getServiceInstanceCount;
    $[27] = handleServiceClick;
    $[28] = removeService;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const handleQuantityChange = t6;
  let t7;
  if ($[30] !== onOpenDetails) {
    t7 = (e, serviceId_0) => {
      e.stopPropagation();
      e.preventDefault();
      onOpenDetails?.(serviceId_0);
    };
    $[30] = onOpenDetails;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const handleInfoClick = t7;
  const canSelectMore = selectedServiceIds.length < effectiveNumAllowedServices;
  let t8;
  if ($[32] !== data?.providers || $[33] !== data?.services || $[34] !== providerFilterId || $[35] !== selectedLocation?.id || $[36] !== selectedLocationId || $[37] !== selectedServiceIds || $[38] !== selectedServices) {
    t8 = () => getUnavailableServiceIds({
      services: data?.services,
      providers: data?.providers,
      selectedServices,
      selectedServiceIds,
      selectedLocationId: selectedLocation?.id ?? selectedLocationId,
      selectedProviderId: providerFilterId
    });
    $[32] = data?.providers;
    $[33] = data?.services;
    $[34] = providerFilterId;
    $[35] = selectedLocation?.id;
    $[36] = selectedLocationId;
    $[37] = selectedServiceIds;
    $[38] = selectedServices;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  data?.providers;
  data?.services;
  selectedLocation?.id;
  let t9;
  if ($[40] !== t8) {
    t9 = t8();
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  const unavailableServiceIds = t9;
  let t10;
  if ($[42] !== unavailableServiceIds) {
    t10 = id => unavailableServiceIds.has(id);
    $[42] = unavailableServiceIds;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  const isServiceUnavailable = t10;
  const params = useParams();
  const t11 = params?.tenant;
  let t12;
  if ($[44] !== t11) {
    t12 = usesSelfScheduleCategoryHierarchy(t11);
    $[44] = t11;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  const useCategoryHierarchy = t12;
  data?.services;
  let t13;
  if ($[46] !== data?.services) {
    t13 = data?.services ?? [];
    $[46] = data?.services;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const services_0 = t13;
  let t14;
  if ($[48] !== services_0) {
    t14 = services_0.some(_temp);
    $[48] = services_0;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  const hasCategories = t14;
  let t15;
  if ($[50] !== services_0) {
    t15 = services_0.some(_temp2);
    $[50] = services_0;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  const hasTags = t15;
  const shouldUseLargeTaggedLayout = useCategoryHierarchy || services_0.length > TAGGED_SERVICES_THRESHOLD && hasCategories && hasTags;
  let t16;
  if ($[52] !== searchQuery) {
    t16 = searchQuery.trim();
    $[52] = searchQuery;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  const t17 = t16.length > 0;
  let t18;
  if ($[54] !== canSelectMore || $[55] !== categories || $[56] !== effectiveNumAllowedServices || $[57] !== effectiveSelectedCategory || $[58] !== filteredServices || $[59] !== frontPageData || $[60] !== getServiceInstanceCount || $[61] !== handleInfoClick || $[62] !== handleQuantityChange || $[63] !== handleServiceClick || $[64] !== isLoading || $[65] !== isServiceSelected || $[66] !== isServiceUnavailable || $[67] !== selectedServiceIds || $[68] !== shouldUseLargeTaggedLayout || $[69] !== t17 || $[70] !== useCategoryHierarchy || $[71] !== userLocation) {
    t18 = {
      frontPageData,
      categories,
      filteredServices,
      isSearching: t17,
      isLoading,
      selectedCategory: effectiveSelectedCategory,
      isServiceSelected,
      getServiceInstanceCount,
      isServiceUnavailable,
      selectedServiceIds,
      canSelectMore,
      userLocation,
      effectiveNumAllowedServices,
      shouldUseLargeTaggedLayout,
      useCategoryHierarchy,
      handleServiceClick,
      handleQuantityChange,
      handleInfoClick
    };
    $[54] = canSelectMore;
    $[55] = categories;
    $[56] = effectiveNumAllowedServices;
    $[57] = effectiveSelectedCategory;
    $[58] = filteredServices;
    $[59] = frontPageData;
    $[60] = getServiceInstanceCount;
    $[61] = handleInfoClick;
    $[62] = handleQuantityChange;
    $[63] = handleServiceClick;
    $[64] = isLoading;
    $[65] = isServiceSelected;
    $[66] = isServiceUnavailable;
    $[67] = selectedServiceIds;
    $[68] = shouldUseLargeTaggedLayout;
    $[69] = t17;
    $[70] = useCategoryHierarchy;
    $[71] = userLocation;
    $[72] = t18;
  } else {
    t18 = $[72];
  }
  return t18;
}
function _temp2(service_0) {
  return (service_0.tags?.length ?? 0) > 0;
}
function _temp(service) {
  return !!service.category;
}